import { useEffect, useState } from "react";
import { useProgressBootstrap } from "@/application/usecases/useProgressBootstrap";
import { useSettingsSync } from "@/application/usecases/useSettingsSync";
import { useSkillMemoryBootstrap } from "@/application/usecases/useSkillMemoryBootstrap";
import { MobileGuideOverlay } from "@/presentation/features/mobile-guide/MobileGuideOverlay";
import { useIsMobileOrNarrow } from "@/presentation/features/mobile-guide/useIsMobileOrNarrow";
import { MusicConsentOverlay } from "@/presentation/features/music-consent/MusicConsentOverlay";
import { KeyListModal } from "@/presentation/features/settings/KeyListModal";
import { OverlayProvider, useOverlay } from "./components/overlay/OverlayProvider";
import { Toast } from "./components/overlay/Toast";
import { useRouter } from "./router/useRouter";
import { LevelScreen } from "./screens/Level/LevelScreen";
import { LevelSelect } from "./screens/LevelSelect/LevelSelect";
import { ReviewScreen } from "./screens/Review/ReviewScreen";
import { StampBook } from "./screens/StampBook/StampBook";
import { TitleMenu } from "./screens/TitleMenu/TitleMenu";
import "./styles/tokens.css";
import "./styles/reset.css";

const INTERRUPT_MESSAGE = "中断しました。つづきから再開できます";
/** E-6（進捗データ破損）。ui-design.md §10「主要な文言」の表記と一致させる。 */
const PROGRESS_RECOVERY_MESSAGE = "稽古の記録を読み込めなかったため、初めから始めます";
/** E-7（localStorage不可）。同上。 */
const STORAGE_UNAVAILABLE_MESSAGE = "この設定では記録を残せません。この場での稽古は続けられます";

/**
 * S-0（`/`）・S-1（`/levels`）・S-2（`/level/:levelId`）・S-3（`/stamps`）・S-4（`/review`）を
 * URLを持つ画面として結線する。
 */
export function App() {
  return (
    <OverlayProvider>
      <AppShell />
    </OverlayProvider>
  );
}

function AppShell() {
  const { route, navigate, interruptedFromLevelCount } = useRouter();
  const { toast, showToast } = useOverlay();
  const [keyListOpen, setKeyListOpen] = useState(false);
  const isMobileOrNarrow = useIsMobileOrNarrow();
  const { recoveryError, storageUnavailable } = useProgressBootstrap();
  useSkillMemoryBootstrap();
  useSettingsSync();

  // biome-ignore lint/correctness/useExhaustiveDependencies: interruptedFromLevelCountの変化そのものがトリガー
  useEffect(() => {
    if (interruptedFromLevelCount > 0) {
      showToast({ kind: "info", message: INTERRUPT_MESSAGE });
    }
  }, [interruptedFromLevelCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: showToastはOverlayProviderのuseCallbackで安定した参照
  useEffect(() => {
    if (storageUnavailable) {
      showToast({ kind: "info", message: STORAGE_UNAVAILABLE_MESSAGE });
    } else if (recoveryError) {
      showToast({ kind: "info", message: PROGRESS_RECOVERY_MESSAGE });
    }
  }, [recoveryError, storageUnavailable]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "?") {
        return;
      }
      const active = document.activeElement;
      if (
        active?.getAttribute("role") === "application" ||
        active?.tagName === "INPUT" ||
        active?.tagName === "TEXTAREA" ||
        active?.tagName === "SELECT" ||
        active?.closest(".modal-panel")
      ) {
        return;
      }
      setKeyListOpen(true);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      {route.name === "title" && (
        <TitleMenu
          navigate={navigate}
          onOpenKeyList={() => setKeyListOpen(true)}
          isMobileOrNarrow={isMobileOrNarrow}
        />
      )}
      {route.name === "level-select" && (
        <LevelSelect navigate={navigate} isMobileOrNarrow={isMobileOrNarrow} />
      )}
      {route.name === "stamps" && <StampBook onNavigate={navigate} />}
      {route.name === "review" && <ReviewScreen navigate={navigate} />}
      {route.name === "level" && (
        <LevelScreen key={route.levelId} levelId={route.levelId} navigate={navigate} />
      )}
      {toast && <Toast toast={toast} />}
      <MusicConsentOverlay />
      {isMobileOrNarrow && <MobileGuideOverlay />}
      <KeyListModal open={keyListOpen} onClose={() => setKeyListOpen(false)} />
    </>
  );
}
